import { useMemo } from 'react';

import { useWorkflowPermissionContext } from '../useWorkflowPermissionContext';

export type UseWorkflowPermissionOptions = {
  // What kind of permission to evaluate
  type?: 'action' | 'function';

  // The action or function which we are checking permissions for
  name?: string;

  // Optimistic permissions. If true, we will assume that the user has
  // permissions to perform the action/function. If false, we will assume that
  // the user does not have permissions to perform the action/function.
  optimistic?: boolean;
};

export type UseWorkflowPermissionResult = {
  allowed: boolean;
  missingPermissions?: PermissionCheck[];
};

type PermissionCheck = {
  isAllowed: boolean;
  relation: string;
  resource: string;
};

export function useWorkflowPermission({
  type,
  name,
  optimistic = false,
}: UseWorkflowPermissionOptions): UseWorkflowPermissionResult {
  const checkPermissions = useWorkflowPermissionContext();

  const result: UseWorkflowPermissionResult = useMemo(() => {
    if (checkPermissions?.isAllAllowed) {
      return { allowed: true };
    }

    let permissionChecks: PermissionCheck[] = [];
    let isAllowed = false;

    if (type === 'action' && checkPermissions?.actionPermissionCheckResults) {
      const actionResult = checkPermissions.actionPermissionCheckResults.find(
        checkResult => checkResult.action === name,
      );
      isAllowed = actionResult?.isAllowed ?? false;
      permissionChecks =
        actionResult?.permissionChecks.map(checkResult => {
          return {
            isAllowed: checkResult.isAllowed || false,
            relation: checkResult.relation || '',
            resource: checkResult.resource || '',
          };
        }) ?? [];
    }

    if (
      type === 'function' &&
      checkPermissions?.functionPermissionCheckResults
    ) {
      const functionResult =
        checkPermissions.functionPermissionCheckResults.find(
          checkResult => checkResult.function === name,
        );
      isAllowed = functionResult?.isAllowed ?? false;
      permissionChecks =
        functionResult?.permissionChecks.map(checkResult => {
          return {
            isAllowed: checkResult.isAllowed || false,
            relation: checkResult.relation || '',
            resource: checkResult.resource || '',
          };
        }) ?? [];
    }

    if (!type || !name) {
      isAllowed = optimistic;
    }

    return {
      allowed: isAllowed,
      missingPermissions: isAllowed
        ? undefined
        : permissionChecks.filter(check => !check.isAllowed),
    };
  }, [checkPermissions, type, name, optimistic]);

  return result;
}
